/**
 * Shared Snap annotation model — single edit graph for native Panel, KitchenPro SDK,
 * and future framework wrappers. Strokes are flattened to PNG before upload; the server
 * stores only the final image + sanitized metadata (no second annotation SoR).
 */

export type AnnotationTool =
  | 'select'
  | 'crop'
  | 'rect'
  | 'arrow'
  | 'freehand'
  | 'text'
  | 'cover'
  | null;

export type AnnotationEdit =
  | { type: 'crop'; x: number; y: number; w: number; h: number }
  | { type: 'rect'; x: number; y: number; w: number; h: number }
  | { type: 'arrow'; x: number; y: number; x2: number; y2: number }
  | { type: 'freehand'; points: Array<{ x: number; y: number }> }
  | { type: 'text'; x: number; y: number; text: string }
  | { type: 'cover'; x: number; y: number; w: number; h: number };

export class AnnotationSession {
  private original: HTMLCanvasElement | null = null;
  private working: HTMLCanvasElement | null = null;
  private edits: AnnotationEdit[] = [];
  /** Redo stack — edits removed by undo live here until a new stroke clears them. */
  private redoStack: AnnotationEdit[] = [];
  private tool: AnnotationTool = null;
  private strokeStart: { x: number; y: number } | null = null;
  private freehandPoints: Array<{ x: number; y: number }> = [];
  private drawing = false;
  private selectedIndex: number | null = null;
  private listeners = new Set<() => void>();

  get canvas(): HTMLCanvasElement | null {
    return this.working;
  }

  get editCount(): number {
    return this.edits.length;
  }

  get selectedEditIndex(): number | null {
    return this.selectedIndex;
  }

  get hasRedaction(): boolean {
    return this.edits.some((e) => e.type === 'cover');
  }

  get hasAnnotation(): boolean {
    return this.edits.some((e) => e.type !== 'cover' && e.type !== 'crop');
  }

  get hasCrop(): boolean {
    return this.edits.some((e) => e.type === 'crop');
  }

  get canUndo(): boolean {
    return this.edits.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Snapshot of edits (immutable copy) for hosts that need metadata flags. */
  getEdits(): readonly AnnotationEdit[] {
    return this.edits.slice();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  setSource(source: HTMLCanvasElement): void {
    this.clearMemory();
    this.original = cloneCanvas(source);
    this.working = cloneCanvas(source);
    this.edits = [];
    this.redoStack = [];
    this.selectedIndex = null;
    this.notify();
  }

  setTool(tool: AnnotationTool): void {
    this.tool = tool;
    if (tool !== 'select') {
      this.selectedIndex = null;
    }
    this.notify();
  }

  getTool(): AnnotationTool {
    return this.tool;
  }

  undo(): void {
    const last = this.edits.pop();
    if (!last) return;
    this.redoStack.push(last);
    this.selectedIndex = null;
    this.redraw();
    this.notify();
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.edits.push(next);
    this.selectedIndex = null;
    this.redraw();
    this.notify();
  }

  clear(): void {
    if (!this.edits.length && !this.redoStack.length) return;
    this.edits = [];
    this.redoStack = [];
    this.selectedIndex = null;
    this.redraw();
    this.notify();
  }

  deleteSelected(): boolean {
    if (this.selectedIndex == null || this.selectedIndex < 0 || this.selectedIndex >= this.edits.length) {
      return false;
    }
    const [removed] = this.edits.splice(this.selectedIndex, 1);
    this.redoStack.push(removed);
    this.selectedIndex = null;
    this.redraw();
    this.notify();
    return true;
  }

  selectAt(x: number, y: number): number | null {
    for (let i = this.edits.length - 1; i >= 0; i--) {
      if (hitTest(this.edits[i], x, y)) {
        this.selectedIndex = i;
        this.redraw();
        this.notify();
        return i;
      }
    }
    this.selectedIndex = null;
    this.redraw();
    this.notify();
    return null;
  }

  clearMemory(): void {
    this.original = null;
    this.working = null;
    this.edits = [];
    this.redoStack = [];
    this.tool = null;
    this.strokeStart = null;
    this.freehandPoints = [];
    this.drawing = false;
    this.selectedIndex = null;
  }

  /** Flatten annotations into a deterministic canvas for submission. */
  flatten(): HTMLCanvasElement | null {
    this.redraw();
    return this.working ? cloneCanvas(this.working) : null;
  }

  /** Paint current working buffer onto a display canvas (keeps pointer binding target). */
  paintTo(canvas: HTMLCanvasElement): void {
    if (!this.working) return;
    canvas.width = this.working.width;
    canvas.height = this.working.height;
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    ctx?.drawImage(this.working, 0, 0);
    if (this.selectedIndex != null && this.edits[this.selectedIndex]) {
      drawSelection(ctx!, this.edits[this.selectedIndex]);
    }
  }

  bindCanvas(canvas: HTMLCanvasElement): () => void {
    const onDown = (evt: PointerEvent) => this.onPointerDown(canvas, evt);
    const onMove = (evt: PointerEvent) => this.onPointerMove(canvas, evt);
    const onUp = (evt: PointerEvent) => this.onPointerUp(canvas, evt);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    canvas.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }

  private point(canvas: HTMLCanvasElement, evt: PointerEvent) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / Math.max(1, rect.width);
    const scaleY = canvas.height / Math.max(1, rect.height);
    return {
      x: Math.round((evt.clientX - rect.left) * scaleX),
      y: Math.round((evt.clientY - rect.top) * scaleY)
    };
  }

  private pushEdit(edit: AnnotationEdit): void {
    this.edits.push(edit);
    this.redoStack = [];
    this.selectedIndex = null;
  }

  private onPointerDown(canvas: HTMLCanvasElement, evt: PointerEvent): void {
    if (!this.tool) return;
    if (this.tool === 'select') {
      const p = this.point(canvas, evt);
      this.selectAt(p.x, p.y);
      this.paintTo(canvas);
      return;
    }
    this.drawing = true;
    const p = this.point(canvas, evt);
    this.strokeStart = p;
    if (this.tool === 'freehand') {
      this.freehandPoints = [p];
    }
    if (this.tool === 'text') {
      const text = window.prompt('Annotation text', 'Note') || '';
      if (text.trim()) {
        this.pushEdit({ type: 'text', x: p.x, y: p.y, text: text.slice(0, 80) });
        this.redraw();
        this.paintTo(canvas);
        this.notify();
      }
      this.drawing = false;
      this.strokeStart = null;
    }
  }

  private onPointerMove(canvas: HTMLCanvasElement, evt: PointerEvent): void {
    if (!this.drawing || !this.tool || !this.strokeStart || this.tool === 'select' || this.tool === 'text') {
      return;
    }
    const p = this.point(canvas, evt);
    if (this.tool === 'freehand') {
      this.freehandPoints.push(p);
    }
    this.redraw();
    const ctx = this.working?.getContext('2d');
    if (!ctx) return;
    if (this.tool === 'rect' || this.tool === 'cover' || this.tool === 'crop') {
      const x = Math.min(this.strokeStart.x, p.x);
      const y = Math.min(this.strokeStart.y, p.y);
      const w = Math.abs(p.x - this.strokeStart.x);
      const h = Math.abs(p.y - this.strokeStart.y);
      if (this.tool === 'cover') {
        ctx.fillStyle = '#000';
        ctx.fillRect(x, y, w, h);
      } else if (this.tool === 'crop') {
        ctx.strokeStyle = '#228be6';
        ctx.setLineDash([6, 4]);
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 1, y + 1, Math.max(0, w - 2), Math.max(0, h - 2));
        ctx.setLineDash([]);
      } else {
        ctx.strokeStyle = '#e03131';
        ctx.lineWidth = 3;
        ctx.strokeRect(x + 1.5, y + 1.5, Math.max(0, w - 3), Math.max(0, h - 3));
      }
    } else if (this.tool === 'arrow') {
      drawArrow(ctx, this.strokeStart.x, this.strokeStart.y, p.x, p.y);
    } else if (this.tool === 'freehand') {
      ctx.strokeStyle = '#e03131';
      ctx.lineWidth = 3;
      ctx.beginPath();
      this.freehandPoints.forEach((pt, i) => {
        if (i === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      });
      ctx.stroke();
    }
    this.paintTo(canvas);
  }

  private onPointerUp(canvas: HTMLCanvasElement, evt: PointerEvent): void {
    if (!this.drawing || !this.tool || !this.strokeStart || this.tool === 'select' || this.tool === 'text') {
      this.drawing = false;
      return;
    }
    const p = this.point(canvas, evt);
    if (this.tool === 'rect' || this.tool === 'cover' || this.tool === 'crop') {
      const x = Math.min(this.strokeStart.x, p.x);
      const y = Math.min(this.strokeStart.y, p.y);
      const w = Math.abs(p.x - this.strokeStart.x);
      const h = Math.abs(p.y - this.strokeStart.y);
      if (w > 2 && h > 2) {
        this.pushEdit({
          type: this.tool === 'cover' ? 'cover' : this.tool === 'crop' ? 'crop' : 'rect',
          x,
          y,
          w,
          h
        });
      }
    } else if (this.tool === 'arrow') {
      this.pushEdit({
        type: 'arrow',
        x: this.strokeStart.x,
        y: this.strokeStart.y,
        x2: p.x,
        y2: p.y
      });
    } else if (this.tool === 'freehand' && this.freehandPoints.length > 1) {
      this.pushEdit({ type: 'freehand', points: [...this.freehandPoints] });
    }
    this.drawing = false;
    this.strokeStart = null;
    this.freehandPoints = [];
    this.redraw();
    this.paintTo(canvas);
    this.notify();
  }

  private redraw(): void {
    if (!this.original) return;
    let base = cloneCanvas(this.original);
    const crops = this.edits.filter((e): e is Extract<AnnotationEdit, { type: 'crop' }> => e.type === 'crop');
    // Apply crops in order by rebasing the canvas (matches native snap-screen behavior).
    for (const crop of crops) {
      base = cropCanvas(base, crop.x, crop.y, crop.w, crop.h);
    }
    this.working = base;
    const ctx = this.working.getContext('2d');
    if (!ctx) return;
    for (const edit of this.edits) {
      if (edit.type === 'crop') continue;
      if (edit.type === 'cover') {
        ctx.fillStyle = '#000';
        ctx.fillRect(edit.x, edit.y, edit.w, edit.h);
      } else if (edit.type === 'rect') {
        ctx.strokeStyle = '#e03131';
        ctx.lineWidth = 3;
        ctx.strokeRect(edit.x + 1.5, edit.y + 1.5, Math.max(0, edit.w - 3), Math.max(0, edit.h - 3));
      } else if (edit.type === 'arrow') {
        drawArrow(ctx, edit.x, edit.y, edit.x2, edit.y2);
      } else if (edit.type === 'text') {
        ctx.fillStyle = '#e03131';
        ctx.font = 'bold 18px sans-serif';
        ctx.fillText(edit.text || 'Note', edit.x, edit.y);
      } else if (edit.type === 'freehand') {
        ctx.strokeStyle = '#e03131';
        ctx.lineWidth = 3;
        ctx.beginPath();
        edit.points.forEach((pt, i) => {
          if (i === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        });
        ctx.stroke();
      }
    }
  }
}

function cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = source.width;
  c.height = source.height;
  try {
    const ctx = c.getContext('2d');
    if (ctx) ctx.drawImage(source, 0, 0);
  } catch {
    /* jsdom / headless may lack canvas 2d */
  }
  return c;
}

function cropCanvas(
  source: HTMLCanvasElement,
  x: number,
  y: number,
  w: number,
  h: number
): HTMLCanvasElement {
  const sx = Math.max(0, Math.min(source.width - 1, Math.round(x)));
  const sy = Math.max(0, Math.min(source.height - 1, Math.round(y)));
  const sw = Math.max(1, Math.min(source.width - sx, Math.round(w)));
  const sh = Math.max(1, Math.min(source.height - sy, Math.round(h)));
  const c = document.createElement('canvas');
  c.width = sw;
  c.height = sh;
  try {
    const ctx = c.getContext('2d');
    if (ctx) ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
  } catch {
    /* jsdom / headless may lack canvas 2d */
  }
  return c;
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): void {
  const head = 14;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.strokeStyle = '#e03131';
  ctx.fillStyle = '#e03131';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

function hitTest(edit: AnnotationEdit, x: number, y: number): boolean {
  const pad = 8;
  if (edit.type === 'rect' || edit.type === 'cover' || edit.type === 'crop') {
    return x >= edit.x - pad && x <= edit.x + edit.w + pad && y >= edit.y - pad && y <= edit.y + edit.h + pad;
  }
  if (edit.type === 'text') {
    return Math.abs(x - edit.x) < 60 && Math.abs(y - edit.y) < 24;
  }
  if (edit.type === 'arrow') {
    return pointNearSegment(x, y, edit.x, edit.y, edit.x2, edit.y2, pad + 4);
  }
  if (edit.type === 'freehand') {
    for (let i = 1; i < edit.points.length; i++) {
      const a = edit.points[i - 1];
      const b = edit.points[i];
      if (pointNearSegment(x, y, a.x, a.y, b.x, b.y, pad)) return true;
    }
  }
  return false;
}

function pointNearSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  tol: number
): boolean {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1) <= tol;
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx;
  const cy = y1 + t * dy;
  return Math.hypot(px - cx, py - cy) <= tol;
}

function drawSelection(ctx: CanvasRenderingContext2D, edit: AnnotationEdit): void {
  ctx.save();
  ctx.strokeStyle = '#4dabf7';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 3]);
  if (edit.type === 'rect' || edit.type === 'cover' || edit.type === 'crop') {
    ctx.strokeRect(edit.x - 2, edit.y - 2, edit.w + 4, edit.h + 4);
  } else if (edit.type === 'text') {
    ctx.strokeRect(edit.x - 4, edit.y - 18, 80, 24);
  } else if (edit.type === 'arrow') {
    ctx.beginPath();
    ctx.moveTo(edit.x, edit.y);
    ctx.lineTo(edit.x2, edit.y2);
    ctx.stroke();
  } else if (edit.type === 'freehand' && edit.points.length) {
    let minX = edit.points[0].x;
    let minY = edit.points[0].y;
    let maxX = minX;
    let maxY = minY;
    for (const p of edit.points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    ctx.strokeRect(minX - 4, minY - 4, maxX - minX + 8, maxY - minY + 8);
  }
  ctx.restore();
}
